import { Button } from '@synoptic/ui-kit/button/button.tsx';
import { useIsTablet } from '@synoptic/ui-kit/responsive/hooks.ts';
import { clsx } from 'clsx';
import { FC, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import {
  StripeBillingInterval,
  StreamQueryQuery,
  StreamState,
  StreamType,
} from '@/__generated__/graphql.ts';
import { LayoutCard } from '@/components/layout/card.tsx';
import { StreamLabel } from '@/components/post-label/stream-label.tsx';
import { TargetsModal } from '@/components/streams/custom-stream/targets-modal.tsx';
import { BannerSocialActions } from '@/components/streams/stream-banner/banner-social-actions.tsx';
import { hasRightsToStream } from '@/components/streams/stream-banner/has-rights-to-stream';
import { SubscriptionExpiryNotice } from '@/components/streams/stream-banner/subscription-expiry-notice.tsx';
import { StreamPriceLabel } from '@/components/streams/stream-tag/stream-price-label.tsx';
import { StreamSoundButton } from '@/components/streams/toggle-notif-sound/stream-sound-button.tsx';
import { SubscribePaidStreamButton } from '@/components/streams/toggle-paid-stream-subscription/subscribe-paid-stream-button.tsx';
import { IncludedInPackageLabel } from '@/routes/_app.p.streams_.$id/stream-banner/included-in-package-label.tsx';
import { MeStreamActions } from '@/routes/_app.p.streams_.$id/stream-banner/me-stream-actions.tsx';
import { StreamBannerContent } from '@/routes/_app.p.streams_.$id/stream-banner/stream-banner-content.tsx';
import {
  buttonsBlock,
  followStreamButton,
  labelsContainer,
  paidBannerFooterSection,
  paidStreamBannerCard,
  paidStreamBannerFooter,
  paidBannerActionsBlock,
  paidStreamLabel,
  paidStreamLabelContainer,
} from '@/routes/_app.p.streams_.$id/stream-banner/stream-banner.css.ts';
import { targetsPageAction } from '@/routes/_app.p.streams_.$id_.targets/page.css.ts';
import { convertCentsToDollars } from '@/utils/convert-cents-to-dollars.ts';
import {
  getMonthlyPrice,
  getSubscriptionTotalPrice,
} from '@/utils/paid-data.ts';
import { MonitorPaidStreamAccess } from '../monitor-paid-stream-access';

export const PaidStreamBanner: FC<{
  stream: NonNullable<StreamQueryQuery['stream']>;
  isMe: boolean;
  toggleFollow: () => void;
  followLoading?: boolean;
}> = ({ stream, isMe, toggleFollow, followLoading }) => {
  const isTablet = useIsTablet();
  const isDraft = stream?.streamState === StreamState.Draft;

  const [searchParams] = useSearchParams();
  const paymentSuccess = searchParams.get('success') === 'true';

  useEffect(() => {
    if (!isMe && !paymentSuccess) {
      window.mixpanel?.track('Paid Stream Page Visited', {
        'Stream ID': stream.id,
        'Access Allowed': stream.accessAllowed,
      });
    }
  }, [isMe, paymentSuccess, stream.accessAllowed, stream.id]);

  if (!stream.paidData?.length) {
    return null;
  }

  const hasRights = hasRightsToStream(stream);

  const showIncludedInPackageLabel =
    stream.inPackages?.length && (!isTablet || (!isMe && !hasRights));

  const showSoundButton =
    stream.accessAllowed &&
    (!stream.customTargetsData || stream.customTargets?.length);

  const showPrice = !isTablet || (!isMe && !hasRights);

  const hasProvidedTargets = !!stream.customTargets?.length;
  const showTargetsButton = !isMe && hasRights && !!stream.customTargetsData;

  const showCancelAt =
    stream.subscription?.active && stream.subscription.cancelAt && !isMe;

  return (
    <LayoutCard className={paidStreamBannerCard}>
      {!!stream && (
        <StreamBannerContent
          activeSubscription={hasRights}
          stream={stream}
          isMe={isMe}
        />
      )}
      <div className={paidStreamBannerFooter}>
        <div className={paidBannerFooterSection}>
          <BannerSocialActions
            streamId={stream.id}
            followersCount={stream.followersCount}
            isPrivate={stream.streamType === StreamType.Private}
            isAuthor={isMe}
            isDraft={isDraft}
            isPaid
          />
        </div>

        <div
          className={clsx(paidStreamLabelContainer, paidBannerFooterSection)}
        >
          <StreamLabel
            label={stream.label}
            streamId={stream.id}
            className={paidStreamLabel}
          />
        </div>

        <div className={clsx(paidBannerActionsBlock, paidBannerFooterSection)}>
          <div className={labelsContainer}>
            {showCancelAt ? (
              <SubscriptionExpiryNotice
                cancelAt={stream.subscription?.cancelAt as string}
              />
            ) : showPrice ? (
              <StreamPriceLabel
                price={convertCentsToDollars(
                  hasRights && stream.subscription
                    ? getSubscriptionTotalPrice(
                        stream.subscription,
                        stream.customTargetsData,
                      )
                    : getMonthlyPrice(stream.paidData)!,
                )}
                isStartingPrice={!!stream.customTargetsData}
                variant={hasRights || isMe ? 'secondary' : 'primary'}
                subscribed={hasRights && !isMe}
                interval={
                  hasRights && stream.subscription
                    ? stream.subscription.interval
                    : StripeBillingInterval.Month
                }
                intervalCount={
                  hasRights && stream.subscription
                    ? stream.subscription.intervalCount
                    : 1
                }
              />
            ) : null}

            {showIncludedInPackageLabel ? (
              <IncludedInPackageLabel
                packages={stream.inPackages}
                packageOnly={stream.packageOnly}
              />
            ) : null}
          </div>

          <div className={buttonsBlock}>
            {showSoundButton ? (
              <StreamSoundButton streamId={stream.id} />
            ) : null}

            {showTargetsButton ? (
              <TargetsModal
                instruction={stream.customTargetsData?.targetTypeInstruction}
                maxTargets={stream.subscription?.customTargets ?? 0}
                initialTargets={stream.customTargets || []}
                targetValidationRegex={
                  stream.customTargetsData?.targetValidationRegex
                }
                customTargetsData={stream.customTargetsData}
                subscription={stream.subscription}
                trigger={
                  <Button
                    variant={'secondary'}
                    className={targetsPageAction}
                    size={isTablet ? 'medium' : 'small'}
                    fullWidth={!isTablet}
                  >
                    {hasProvidedTargets ? 'Edit targets' : 'Provide targets'}
                  </Button>
                }
                refetchStreamPosts
              />
            ) : null}

            {!isMe ? (
              !hasRights ? (
                <SubscribePaidStreamButton
                  streamId={stream.id}
                  loading={paymentSuccess}
                  packages={stream.inPackages}
                  prices={stream.paidData ?? []}
                  customTargets={stream.customTargetsData ?? undefined}
                />
              ) : (
                <Button
                  onClick={toggleFollow}
                  variant={'primary'}
                  size={isTablet ? 'medium' : 'small'}
                  className={followStreamButton}
                  disabled={isDraft && !stream.following}
                  loading={followLoading}
                >
                  {!stream.following ? 'Follow' : 'Following'}
                </Button>
              )
            ) : (
              <MeStreamActions stream={stream} />
            )}
          </div>
        </div>
      </div>

      {!isMe && (
        <MonitorPaidStreamAccess
          streamId={stream.id}
          accessAllowed={stream.accessAllowed}
        />
      )}
    </LayoutCard>
  );
};
